import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import type { SisDistrict, SisField } from './sisMatchingApi';
import { fieldSubLabel, TIERS } from './sisMatchingView';
import { SIS, sisText } from './sisTokens';

type Props = {
  district: SisDistrict;
  active: string;
  pathFor: (field: string) => string;
};

function Icon({ field }: { field: SisField }) {
  const sx = { width: 20, height: 20, flexShrink: 0 } as const;
  if (field.state === 'none') return <RemoveCircleOutlineIcon aria-hidden="true" sx={{ ...sx, color: SIS.disabled }} />;
  if (field.state === 'ok') return <CheckCircleOutlineIcon aria-hidden="true" sx={{ ...sx, color: SIS.success }} />;
  return <ErrorOutlineIcon aria-hidden="true" sx={{ ...sx, color: SIS.warningDark }} />;
}

export function FieldList({ district, active, pathFor }: Props) {
  return (
    <Box component="nav" aria-label="Fields" sx={{ width: 256, flexShrink: 0, padding: '8px 0', display: 'flex', flexDirection: 'column', bgcolor: SIS.surface, borderRadius: '8px', boxShadow: SIS.cardShadow }}>
      {TIERS.map((tier, index) => {
        const fields = district.fields.filter((f) => f.tier === tier.tier);
        const answered = fields.filter((f) => f.answered).length;
        return (
          <Box key={tier.tier} sx={{ display: 'flex', flexDirection: 'column', boxShadow: index > 0 ? `inset 0 1px 0 ${SIS.divider}` : 'none' }}>
            <Typography sx={{ ...sisText.eyebrow, padding: index > 0 ? '16px 16px 4px' : '12px 16px 4px' }}>{`${tier.label} · ${answered} of ${fields.length}`}</Typography>
            {fields.map((field) => {
              const on = field.key === active;
              const sub = fieldSubLabel(field, district.columns);
              return (
                <Link
                  key={field.key}
                  component={RouterLink}
                  to={pathFor(field.key)}
                  aria-current={on ? 'true' : undefined}
                  underline="none"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '8px 16px',
                    borderLeft: `2px solid ${on ? SIS.primary : 'transparent'}`,
                    bgcolor: on ? SIS.selectedBg : 'transparent',
                    '&:hover': { bgcolor: on ? SIS.selectedBg : SIS.hoverBg },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography sx={{ ...sisText.body1, fontWeight: 600, color: on ? SIS.primary : SIS.ink }}>{field.label}</Typography>
                    <Typography sx={{ ...sisText.body2, color: sub.warn ? SIS.warningDark : SIS.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.text}</Typography>
                  </Box>
                  <Icon field={field} />
                </Link>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
